import { create } from '@bufbuild/protobuf'
import { TimestampSchema, type Timestamp } from '@bufbuild/protobuf/wkt'
import {
  BillingAccessStateEnum,
  BillingIntervalEnum,
  PlanKeyEnum,
} from '@/gen/cortado/v1/subscriptions_pb'
import type { OrgBillingDetail, RateException } from '@/gen/cortado/v1/admin_pb'

export type BadgeVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'purple'
  | 'pink'
  | 'orange'

// ---------------------------------------------------------------------------
// Billing access state (subscriptions.org_billing_state.billing_state) -
// extends the leaderboard's subscriptionStatus badge mapping to the new
// BillingAccessStateEnum values (§3.2).
// ---------------------------------------------------------------------------

const BILLING_STATE_META: Record<BillingAccessStateEnum, { label: string; variant: BadgeVariant }> = {
  [BillingAccessStateEnum.BILLING_ACCESS_STATE_UNSPECIFIED]: { label: 'Unknown', variant: 'secondary' },
  [BillingAccessStateEnum.BILLING_ACCESS_STATE_NONE]: { label: 'None', variant: 'secondary' },
  [BillingAccessStateEnum.BILLING_ACCESS_STATE_TRIALING]: { label: 'Trialing', variant: 'info' },
  [BillingAccessStateEnum.BILLING_ACCESS_STATE_ACTIVE]: { label: 'Active', variant: 'success' },
  [BillingAccessStateEnum.BILLING_ACCESS_STATE_GRACE]: { label: 'Grace', variant: 'warning' },
  [BillingAccessStateEnum.BILLING_ACCESS_STATE_PAUSED_PAYMENT]: { label: 'Paused (payment)', variant: 'orange' },
  [BillingAccessStateEnum.BILLING_ACCESS_STATE_READ_ONLY]: { label: 'Read-only', variant: 'orange' },
  [BillingAccessStateEnum.BILLING_ACCESS_STATE_EXPIRED]: { label: 'Expired', variant: 'danger' },
  [BillingAccessStateEnum.BILLING_ACCESS_STATE_COMP]: { label: 'Comp', variant: 'purple' },
}

export function billingStateLabel(state: BillingAccessStateEnum): string {
  return BILLING_STATE_META[state]?.label ?? 'Unknown'
}

export function billingStateBadgeVariant(state: BillingAccessStateEnum): BadgeVariant {
  return BILLING_STATE_META[state]?.variant ?? 'secondary'
}

/**
 * Whether the admin trial-clock controls should be offered from the detail
 * projection available today.
 *
 * COMP is an access overlay, so it must not hide an underlying Stripe trial.
 * Until OrgBillingDetail exposes the mirrored Stripe status directly, require
 * every existing signal of a live trial before enabling the optimistic UI:
 * a Stripe subscription, a started and unexpired trial clock, and a currently
 * active comp/whitelist override. The backend remains the authoritative
 * trialing-status check and will reject a stale inference.
 */
export function canSetBillingStartDate(
  billing: OrgBillingDetail,
  now: Date = new Date()
): boolean {
  const state = billing.billingState
  if (!state) return false
  if (state.state === BillingAccessStateEnum.BILLING_ACCESS_STATE_TRIALING) return true
  if (
    state.state !== BillingAccessStateEnum.BILLING_ACCESS_STATE_COMP ||
    !billing.stripeSubscriptionId ||
    !state.trialStartedAt ||
    !state.firstChargeAt ||
    Number(state.firstChargeAt.seconds) * 1000 <= now.getTime()
  ) {
    return false
  }

  const nowMs = now.getTime()
  return billing.accessOverrides.some((override) => {
    if (override.overrideKind !== 'comp' && override.overrideKind !== 'whitelist') return false
    if (override.revokedAt) return false
    if (override.startsAt && Number(override.startsAt.seconds) * 1000 > nowMs) return false
    return !override.endsAt || Number(override.endsAt.seconds) * 1000 > nowMs
  })
}

export const BILLING_STATE_OPTIONS = (
  Object.values(BillingAccessStateEnum).filter(
    (v): v is BillingAccessStateEnum =>
      typeof v === 'number' && v !== BillingAccessStateEnum.BILLING_ACCESS_STATE_UNSPECIFIED,
  )
).map((value) => ({ value, label: billingStateLabel(value) }))

// ---------------------------------------------------------------------------
// Plan key / billing interval
// ---------------------------------------------------------------------------

const PLAN_KEY_LABELS: Record<PlanKeyEnum, string> = {
  [PlanKeyEnum.PLAN_KEY_UNSPECIFIED]: 'Unassigned',
  [PlanKeyEnum.PLAN_KEY_FOUNDER]: 'Founder',
  [PlanKeyEnum.PLAN_KEY_CCC]: 'Coffee Cart Collective',
  [PlanKeyEnum.PLAN_KEY_STANDARD]: 'Standard',
}

export function planKeyLabel(key: PlanKeyEnum): string {
  return PLAN_KEY_LABELS[key] ?? 'Unassigned'
}

export const PLAN_KEY_OPTIONS = [
  PlanKeyEnum.PLAN_KEY_FOUNDER,
  PlanKeyEnum.PLAN_KEY_CCC,
  PlanKeyEnum.PLAN_KEY_STANDARD,
].map((value) => ({ value, label: planKeyLabel(value) }))

const BILLING_INTERVAL_LABELS: Record<BillingIntervalEnum, string> = {
  [BillingIntervalEnum.BILLING_INTERVAL_UNSPECIFIED]: '—',
  [BillingIntervalEnum.BILLING_INTERVAL_MONTHLY]: 'Monthly',
  [BillingIntervalEnum.BILLING_INTERVAL_SEMIANNUAL]: '6-month (PIF)',
  [BillingIntervalEnum.BILLING_INTERVAL_ANNUAL]: '12-month (PIF)',
}

export function billingIntervalLabel(interval: BillingIntervalEnum): string {
  return BILLING_INTERVAL_LABELS[interval] ?? '—'
}

export function formatUsdCents(cents: number | bigint): string {
  const dollars = Number(cents) / 100
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dollars)
}

// Trial and volume-trial exceptions set a trial; rate exceptions set a price.
export function isTrialException(kind: string): boolean {
  return kind === 'trial' || kind === 'volume_trial'
}

// The approved terms of a trial or volume-trial exception, e.g. "90d" or
// "$5,000.00 or 90d".
export function trialExceptionTerms(
  row: Pick<RateException, 'exceptionKind' | 'approvedTrialDays' | 'approvedVolumeThresholdCents'>,
): string {
  const days = `${row.approvedTrialDays ?? '—'}d`
  if (row.exceptionKind === 'volume_trial' && row.approvedVolumeThresholdCents !== undefined) {
    return `${formatUsdCents(row.approvedVolumeThresholdCents)} or ${days}`
  }
  return days
}

// ---------------------------------------------------------------------------
// Timestamp <-> <input type="date"> helpers
// ---------------------------------------------------------------------------

export function formatDate(ts?: Timestamp): string {
  if (!ts) return '—'
  return new Date(Number(ts.seconds) * 1000).toLocaleDateString(undefined, { dateStyle: 'medium' })
}

export function formatDateTime(ts?: Timestamp): string {
  if (!ts) return '—'
  return new Date(Number(ts.seconds) * 1000).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

export function timestampToDateInputValue(ts?: Timestamp): string {
  if (!ts) return ''
  return new Date(Number(ts.seconds) * 1000).toISOString().slice(0, 10)
}

/** Parses a native `<input type="date">` value (YYYY-MM-DD) as UTC midnight. */
export function dateInputToTimestamp(value: string): Timestamp | undefined {
  if (!value) return undefined
  const date = new Date(`${value}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return undefined
  return create(TimestampSchema, { seconds: BigInt(Math.floor(date.getTime() / 1000)), nanos: 0 })
}

/**
 * Parses a native `<input type="datetime-local">` value (YYYY-MM-DDTHH:mm,
 * no timezone) as the admin operator's local wall-clock time - used for the
 * A9 launch-config timers (§3.10), where the exact time of day matters
 * (DEC-41: Ryan flips the switch ~6-7am ET).
 */
export function dateTimeInputToTimestamp(value: string): Timestamp | undefined {
  if (!value) return undefined
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined
  return create(TimestampSchema, { seconds: BigInt(Math.floor(date.getTime() / 1000)), nanos: 0 })
}

/** Inverse of {@link dateTimeInputToTimestamp} - formats a Timestamp back into local wall-clock time. */
export function timestampToDateTimeInputValue(ts?: Timestamp): string {
  if (!ts) return ''
  const d = new Date(Number(ts.seconds) * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ---------------------------------------------------------------------------
// CSV export (A10) - client-side blob download, no server round-trip.
// ---------------------------------------------------------------------------

function escapeCsvCell(value: string | number): string {
  const text = String(value)
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

export function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]): void {
  const csv = [headers, ...rows].map((row) => row.map(escapeCsvCell).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// ---------------------------------------------------------------------------
// Stripe dashboard deep links (A2). Stripe customer and subscription IDs do
// not encode test vs live mode, so these follow the repository convention of
// linking to the account's default Dashboard mode.
// ---------------------------------------------------------------------------

export function stripeCustomerDashboardUrl(customerId: string): string {
  return `https://dashboard.stripe.com/customers/${customerId}`
}

export function stripeSubscriptionDashboardUrl(subscriptionId: string): string {
  return `https://dashboard.stripe.com/subscriptions/${subscriptionId}`
}

/**
 * Keeps the generated Stripe fields mapped in one place for the billing route.
 */
export function stripeBillingIdentifiers(billing: OrgBillingDetail): {
  customerId?: string
  subscriptionId?: string
} {
  return {
    customerId: billing.stripeCustomerId,
    subscriptionId: billing.stripeSubscriptionId,
  }
}

export type PlanEligibilityListKind = 'founder' | 'ccc'

export function planEligibilityListNoun(listKind: PlanEligibilityListKind): string {
  return listKind === 'founder' ? 'Founder' : 'CCC member'
}

export function planEligibilityAddHint(listKind: PlanEligibilityListKind): string {
  const who = listKind === 'founder' ? 'Founder accounts' : 'CCC members'
  return `New ${who} signing up after launch are added here, then re-priced via Assign Plan on their org's billing detail page.`
}
